import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useInterval } from '@renderer/utils/useInterval'
import { generateUUID } from '@renderer/utils/idUtils'
import cogoToast from '@successtar/cogo-toast'
import Preview from '@renderer/components/Preview'
import Spinner from '@renderer/components/Spinner'
import MidtransUtils from '@renderer/utils/midtransUtils'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { RecordingType } from '@renderer/types/configState'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'
import { useShouldShowLanguageSelector } from '@renderer/hooks'

// Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaLogo from '/assets/bahela/bahela.webp'
import arrowWhite from '/assets/bahela/arrow-white.svg'
import qrisId from '/assets/bahela/qris-holder-id.webp'
import qrisEn from '/assets/bahela/qris-holder-en.webp'

const Welcome: React.FC = () => {
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  // EN/ID toggle is irrelevant for non-IDR projects (we force English
  // via `useEnforceProjectLanguage` in ProjectRoute) — hide it.
  const showLanguageToggle = useShouldShowLanguageSelector()
  const {
    id,
    setSessionState,
    uploadSessionState,
    logActivity,
    transaction,
    userPreferences
  } = useSessionStateStore()
  const { config, filters, frames } = useConfigStore()
  const isMounted = useRef(true)

  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending')
  const [activeFilterIndex, setActiveFilterIndex] = useState(0)
  const [logoClickCount, setLogoClickCount] = useState(0)
  const logoClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [previewKey, setPreviewKey] = useState(0)

  const paymentSettings = getPageSettings('payment')
  const paymentCountdown = useCountdown(paymentSettings?.paymentTimeout || 120)

  const safeUpdate = (callback: () => void): void => {
    if (isMounted.current) callback()
  }

  const cleanupPayment = async (): Promise<void> => {
    if (transaction?.orderId && transaction?.status !== 'settlement') {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${transaction.orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      return Math.max(0, Math.floor((expiryTimestamp - serverTimestamp) / 1000))
    } catch {
      return 0
    }
  }

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  // Ensure session exists with default frame + filter; set transaction amount.
  // Use default (25k) when amount is missing or 0 (e.g. after returning from Result via sessionCleanup).
  const ensureSession = (): void => {
    const sessionId = id || generateUUID()
    const defaultAmount =
      config?.pages?.checkout?.settings?.packages?.find(
        (p: { type: string }) => p.type === 'print'
      )?.price ?? 25000
    const recordingType = config?.pages?.checkout?.settings?.packages?.find(
      (p: { type: string }) =>
        p.type === 'recording' ||
        p.type === 'strip_live' ||
        p.type === 'strip_story' ||
        p.type === 'session_recording'
    )?.type as RecordingType

    const effectiveAmount =
      transaction?.amount != null && transaction.amount > 0 ? transaction.amount : defaultAmount

    setSessionState({
      id: sessionId,
      projectId,
      mode: 'capture',
      timeline: { startTime: new Date() },
      userPreferences: {
        filterId: filters[activeFilterIndex]?._id ?? userPreferences?.filterId ?? filters[0]?._id,
        frameId: frames[0]?._id ?? userPreferences?.frameId,
        useGIF: getPageSettings('capture')?.gifEnabled,
        useRecording: getPageSettings('capture')?.recordingEnabled,
        useVideo: getPageSettings('record')?.enabled,
        recordingType
      },
      gallery: { id: `${9999999999999 - Date.now()}-${sessionId}` },
      transaction: {
        amount: effectiveAmount,
        discount: transaction?.discount ?? 0
      }
    })
  }

  const initiatePayment = async (): Promise<void> => {
    ensureSession()
    const state = useSessionStateStore.getState()
    const sessionId = state.id || id || ''
    const finalAmount = state.transaction?.amount || transaction?.amount || 25000
    try {
      const data = await MidtransUtils.initiatePayment(
        finalAmount,
        sessionId,
        paymentSettings?.paymentTimeout || 120,
        projectId || '',
        state.userPreferences?.filterId || filters[0]?._id || '',
        state.userPreferences?.frameId || frames[0]?._id || '',
        state.transaction?.discount ?? transaction?.discount ?? 0
      )

      if (data.order_id && data.qr_url) {
        const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')
        const initialRemaining = calculateRemainingTime(data.expiry_time, data.server_time)
        if (initialRemaining <= 0) {
          setIsExpired(true)
          return
        }

        setSessionState({
          transaction: {
            orderId: data.order_id,
            qrUrl: sanitizedQrUrl,
            expiryTime: data.expiry_time,
            serverTime: data.server_time,
            status: 'pending',
            amount: finalAmount,
            discount: transaction?.discount || 0
          }
        })
        logActivity('Payment process started', {
          amount: finalAmount,
          orderId: data.order_id
        })

        setQrUrl(sanitizedQrUrl)
        setIsExpired(false)
        paymentCountdown.setRemainingSeconds(initialRemaining)
        paymentCountdown.setOnTick(() => {
          setRemainingTime(formatTime(paymentCountdown.getRemainingSeconds()))
        })
        paymentCountdown.setOnComplete(handleCountdownEnd)
        paymentCountdown.start()
        setIsCheckingPayment(true)
      }
    } catch (e: unknown) {
      console.error('Error initiating payment:', e)
      cogoToast.error((e as Error).message)
    }
  }

  const handleCountdownEnd = async (): Promise<void> => {
    if (!isMounted.current) return
    await checkPaymentStatus()
    safeUpdate(() => {
      setIsCheckingPayment(false)
      setRemainingTime('00:00')
      setIsExpired(true)
      setSessionState({ transaction: { status: 'expire' } })
      logActivity('Payment countdown ended', { orderId: transaction?.orderId })
    })
    // Auto-retry: cleanup expired order, then initiate new payment after short delay
    await cleanupPayment()
    safeUpdate(() => {
      setQrUrl(null)
      setSessionState({ transaction: { orderId: null, status: 'pending' } })
    })
    const retryDelay = 2500
    await new Promise((resolve) => setTimeout(resolve, retryDelay))
    if (!isMounted.current) return
    safeUpdate(() => setIsExpired(false))
    await initiatePayment()
  }

  const checkPaymentStatus = async (isButtonClick = false): Promise<void> => {
    if (!isMounted.current || !transaction?.expiryTime || !transaction?.serverTime) return
    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(
        transaction.orderId || 'order-default-id'
      )
      if (paymentData.isExpired) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setIsExpired(true)
          setSessionState({ transaction: { status: 'expire' } })
          uploadSessionState()
        })
        return
      }
      if (paymentData.paid) {
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setPaymentStatus('success')
          setSessionState({ transaction: { status: 'settlement' } })
          logActivity('Payment completed successfully', {
            orderId: transaction.orderId,
            transactionStatus: paymentData.transactionStatus
          })
          uploadSessionState()
          const tid = setTimeout(() => {
            navigateTimeoutRef.current = null
            if (isMounted.current) navigate(`/${projectId}/capture`)
          }, 2000)
          navigateTimeoutRef.current = tid
        })
      } else if (isButtonClick) {
        setPaymentStatus('failed')
        setTimeout(() => setPaymentStatus('pending'), 2000)
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
      if (isButtonClick) safeUpdate(() => cogoToast.error((e as Error).message))
    }
  }

  const changeLanguage = (lng: string): void => {
    i18n.changeLanguage(lng)
    localStorage.setItem('language', lng)
  }

  const handleLogoClick = (): void => {
    if (logoClickTimeoutRef.current) {
      clearTimeout(logoClickTimeoutRef.current)
      logoClickTimeoutRef.current = null
    }
    const newCount = logoClickCount + 1
    setLogoClickCount(newCount)
    if (newCount >= 5) {
      setLogoClickCount(0)
      ensureSession()
      setSessionState({ transaction: { status: 'settlement' } })
      uploadSessionState()
      logActivity('Payment skipped via logo click (5x)')
      navigate(`/${projectId}/capture`)
      return
    }
    logoClickTimeoutRef.current = setTimeout(() => setLogoClickCount(0), 2000)
  }

  const slidePrev = (): void => {
    if (filters.length === 0) return
    const next = activeFilterIndex <= 0 ? filters.length - 1 : activeFilterIndex - 1
    setActiveFilterIndex(next)
    const filter = filters[next]
    if (filter) {
      setSessionState({ userPreferences: { filterId: filter._id } })
      logActivity(`Selected filter: ${filter.name || filter._id}`, { filterId: filter._id })
    }
  }

  const slideNext = (): void => {
    if (filters.length === 0) return
    const next = (activeFilterIndex + 1) % filters.length
    setActiveFilterIndex(next)
    const filter = filters[next]
    if (filter) {
      setSessionState({ userPreferences: { filterId: filter._id } })
      logActivity(`Selected filter: ${filter.name || filter._id}`, { filterId: filter._id })
    }
  }

  useEffect(() => {
    isMounted.current = true
    // bahela-quick: always start a fresh session on every Welcome load (new id + empty gallery)
    const newId = generateUUID()
    setSessionState({
      id: newId,
      gallery: { id: `${9999999999999 - Date.now()}-${newId}`, capturedItems: [] },
      timeline: { startTime: new Date() }
    })
    ensureSession()
    // Ensure gallery.capturedItems stays empty (ensureSession may overwrite gallery)
    setSessionState({
      gallery: { ...useSessionStateStore.getState().gallery, capturedItems: [] }
    })
    uploadSessionState()
    initiatePayment()
    setPreviewKey((k) => k + 1)
    return (): void => {
      isMounted.current = false
      paymentCountdown.stop()
      cleanupPayment()
      if (logoClickTimeoutRef.current) clearTimeout(logoClickTimeoutRef.current)
      if (navigateTimeoutRef.current) {
        clearTimeout(navigateTimeoutRef.current)
        navigateTimeoutRef.current = null
      }
    }
  }, [])

  useInterval(
    () => checkPaymentStatus(),
    isCheckingPayment ? 5000 : null
  )

  useEffect(() => {
    const filter = filters[activeFilterIndex]
    if (filter)
      setSessionState({ userPreferences: { filterId: filter._id } })
  }, [activeFilterIndex])

  // Key by filter id so Preview remounts on filter change (same as view/Filter.tsx) - avoids flicker
  // from Preview's effect re-running when filter prop changes
  const selectedFilterId = filters[activeFilterIndex]?._id ?? ''

  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedBahelaLogo = useCachedImage(bahelaLogo)
  const cachedArrowWhite = useCachedImage(arrowWhite)
  const cachedQris = useCachedImage(i18n.language === 'id' ? qrisId : qrisEn)
  const cachedQrUrl = useCachedImage(qrUrl || '')

  if (paymentStatus === 'success') {
    return (
      <div className="background relative bg-prominentBlue w-full h-full overflow-hidden flex items-center justify-center">
        <div className="text-uriYellow font-serif-bahela text-5xl">
          {i18n.language === 'id' ? 'Pembayaran berhasil! Mengalihkan...' : 'Payment successful! Redirecting...'}
        </div>
      </div>
    )
  }

  return (
    <div className="background relative bg-prominentBlue w-full h-full overflow-hidden">
      <div
        className="deco-img absolute top-0 left-0 w-full h-full bg-cover z-[0]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      />

      {/* Language toggle - top left. Hidden on non-IDR projects. */}
      {showLanguageToggle && (
        <div className="z-[10] border-[7px] inline-flex drop-shadow-xl items-center border-uriYellow rounded-full text-[36px] p-1 font-sans font-bold absolute top-24 left-20">
          <button
            type="button"
            className={`px-4 py-2 rounded-full w-[77px] h-[56px] flex justify-center items-center ${
              i18n.language === 'id' ? 'bg-uriYellow text-hibiscus' : 'text-uriYellow'
            }`}
            onClick={() => changeLanguage('id')}
          >
            ID
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-full w-[77px] h-[56px] flex justify-center items-center ${
              i18n.language === 'en' ? 'bg-uriYellow text-hibiscus' : 'text-uriYellow'
            }`}
            onClick={() => changeLanguage('en')}
          >
            EN
          </button>
        </div>
      )}

      {/* Yellow logo - top right; 5x click skips payment */}
      <button
        type="button"
        onClick={handleLogoClick}
        className="z-[10] absolute top-20 right-20 p-0 border-0 bg-transparent cursor-pointer"
        aria-label="Bahela logo"
      >
        <img
          src={cachedBahelaLogo}
          alt="Bahela"
          className="object-contain w-32 h-32"
        />
      </button>

      {/* Main: Preview (left) + Payment container (right) - larger boxes */}
      <div className="absolute inset-0 flex items-center justify-center gap-10 pt-24 pb-32 px-16 z-[5]">
        {/* Left: Preview - larger */}
        <div className="flex-shrink-0 w-[540px] flex flex-col items-center">
          <div className="w-full rounded-2xl border-[6px] border-uriYellow bg-black/30 overflow-hidden">
            <div className="aspect-square w-full min-h-[380px] relative bg-gray-800">
              <Preview
                key={`${selectedFilterId}-${previewKey}`}
                adjustments={filters[activeFilterIndex]?.adjustments}
                filter={selectedFilterId}
                postLUTAdjustments={filters[activeFilterIndex]?.postLUTAdjustments}
                ratio={1}
                cameraKey={userPreferences?.selectedCamera as 'primary' | 'secondary'}
              />
            </div>
          </div>
        </div>

        {/* Right: Payment - QRIS holder (larger); Rp + QR aligned to top */}
        <div className="flex-1 flex flex-col items-center max-w-[520px]">
          <div className="relative w-full aspect-square max-w-[500px]">
            <img
              src={cachedQris}
              alt="QRIS Holder"
              className="w-full h-full object-contain"
            />
            <div className="absolute inset-0 flex flex-col items-center pt-[10%]">
              <h2 className="font-serif-bahela text-methadone drop-shadow-2xl text-3xl md:text-4xl mb-2">
                {formatAmount(
                  (transaction?.amount != null && transaction.amount > 0)
                    ? transaction.amount
                    : 25000,
                  projectCurrency
                )}
              </h2>
              {isExpired ? (
                <div className="flex flex-col items-center justify-center gap-4 mt-2">
                  <p className="text-xl font-serif-bahela text-methadone">
                    {i18n.language === 'id' ? 'Kode kedaluwarsa' : 'Code expired'}
                  </p>
                </div>
              ) : qrUrl ? (
                <>
                  <img
                    src={cachedQrUrl}
                    alt="QRIS"
                    className="w-[52%] max-w-[280px] aspect-square object-contain"
                    crossOrigin="anonymous"
                  />
                  <span className="text-[28px] text-white font-serif-bahela mt-2 drop-shadow-2xl">
                    {remainingTime ?? '--:--'}
                  </span>
                </>
              ) : (
                <div className="flex flex-col items-center gap-4 mt-4">
                  <Spinner />
                  <span className="text-white text-lg">Loading QR...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filter carousel - below main; larger options, arrow-white.svg, correct left/right */}
      {filters.length > 0 && (
      <div className="absolute bottom-32 left-0 right-0 flex items-center justify-center gap-6 z-[5]">
        <button
          type="button"
          className="w-20 h-20 rounded-full border-2 border-uriYellow flex items-center justify-center text-white hover:bg-uriYellow/20 transition-colors"
          onClick={slidePrev}
          aria-label="Previous filter"
        >
          <img src={cachedArrowWhite} className="w-12 h-12 object-contain" alt="" />
        </button>
        <div className="flex gap-6 items-center">
          {filters.map((filter, index) => (
            <button
              key={filter._id}
              type="button"
              onClick={() => {
                setActiveFilterIndex(index)
                setSessionState({ userPreferences: { filterId: filter._id } })
              }}
              className={`rounded-full px-10 py-5 border-[4px] border-uriYellow font-serif-bahela text-3xl md:text-4xl transition-all ${
                activeFilterIndex === index
                  ? 'bg-uriYellow text-hibiscus scale-110'
                  : 'bg-uriYellow/20 text-uriYellow'
              }`}
            >
              {filter.name}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="w-20 h-20 rounded-full border-2 border-uriYellow flex items-center justify-center text-white hover:bg-uriYellow/20 transition-colors"
          onClick={slideNext}
          aria-label="Next filter"
        >
          <img src={cachedArrowWhite} className="w-12 h-12 object-contain scale-x-[-1]" alt="" />
        </button>
      </div>
      )}

      {/* Rolling text - bottom */}
      <div className="marquee-container bottom-marquee tracking-[0.28em] z-[2]">
        <div className="marquee-content">
          <span className="font-body-bahela text-uriYellow inline-block text-[38px] italic">
            {i18n.language === 'id'
              ? 'BAHeLA bilik foto lawas untuk menangkap kenangan indahmu bersama orang terkasih di hidupmu'
              : 'BAHeLA vintage photobooth captures beautiful memories with your loved ones'}
          </span>
        </div>
      </div>
    </div>
  )
}

export default Welcome
